import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'success' | 'inherit';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  component?: React.ElementType;
  href?: string;
  target?: string;
  rel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'contained',
      color = 'primary',
      size = 'medium',
      fullWidth = false,
      startIcon,
      endIcon,
      className = '',
      children,
      disabled,
      component: Component = 'button',
      ...props
    },
    ref
  ) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium rounded transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
    
    const sizeClasses = {
      small: 'px-3 py-1.5 text-sm',
      medium: 'px-4 py-2 text-base',
      large: 'px-6 py-3 text-lg',
    };

    const variantClasses = {
      contained: {
        primary: 'bg-[#6f4e37] hover:bg-[#5a3d2b] text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 focus:ring-[#6f4e37] disabled:bg-gray-400 disabled:shadow-none disabled:transform-none',
        secondary: 'bg-[#C68642] hover:bg-[#A66B2E] text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 focus:ring-[#C68642] disabled:bg-gray-400 disabled:shadow-none disabled:transform-none',
        error: 'bg-red-600 text-white shadow-md hover:bg-red-700 hover:shadow-lg hover:-translate-y-0.5 focus:ring-red-500 disabled:bg-gray-400 disabled:shadow-none disabled:transform-none',
        success: 'bg-green-700 text-white shadow-md hover:bg-green-800 hover:shadow-lg hover:-translate-y-0.5 focus:ring-green-500 disabled:bg-gray-400 disabled:shadow-none disabled:transform-none',
        inherit: 'bg-hover-bg text-text-primary shadow-md hover:bg-hover-bg hover:shadow-lg hover:-translate-y-0.5 focus:ring-gray-500 disabled:bg-gray-400 disabled:shadow-none disabled:transform-none',
      },
      outlined: {
        primary: 'border-2 border-card-border bg-transparent text-text-primary hover:bg-hover-bg focus:ring-[#6f4e37] disabled:border-gray-400 disabled:text-gray-400',
        secondary: 'border-2 border-[#C68642] bg-[#C68642]/5 text-[#C68642] hover:bg-[#C68642]/15 hover:border-[#A66B2E] focus:ring-[#C68642] disabled:border-gray-400 disabled:text-gray-400',
        error: 'border-2 border-red-600 bg-red-600/5 text-red-600 hover:bg-red-600/15 hover:border-red-700 focus:ring-red-500 disabled:border-gray-400 disabled:text-gray-400',
        success: 'border-2 border-green-700 bg-green-700/5 text-green-700 hover:bg-green-700/15 hover:border-green-800 focus:ring-green-500 disabled:border-gray-400 disabled:text-gray-400',
        inherit: 'border-2 border-card-border bg-card-bg/5 text-text-primary hover:bg-card-bg hover:border-card-border focus:ring-gray-500 disabled:border-card-border disabled:text-text-tertiary',
      },
      text: {
        primary: 'text-[#6f4e37] hover:bg-[#6f4e37]/10 focus:ring-[#6f4e37] disabled:text-gray-400',
        secondary: 'text-[#C68642] hover:bg-[#C68642]/10 focus:ring-[#C68642] disabled:text-gray-400',
        error: 'text-red-600 hover:bg-red-600/10 focus:ring-red-500 disabled:text-gray-400',
        success: 'text-green-700 hover:bg-green-700/10 focus:ring-green-500 disabled:text-gray-400',
        inherit: 'text-text-secondary hover:bg-card-bg focus:ring-gray-500 disabled:text-text-tertiary',
      },
    };

    const classes = [
      baseClasses,
      sizeClasses[size],
      variantClasses[variant][color],
      fullWidth ? 'w-full' : '',
      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      className,
    ].filter(Boolean).join(' ');

    return (
      <Component
        ref={ref}
        className={classes}
        disabled={disabled}
        {...props}
      >
        {startIcon && <span className="mr-2 flex items-center">{startIcon}</span>}
        {children}
        {endIcon && <span className="ml-2 flex items-center">{endIcon}</span>}
      </Component>
    );
  }
);

Button.displayName = 'Button';

export default Button;
